/**
 * Internal day cost (EUR HT per produced day) — the one setting behind the
 * economic margin (marge contributive − jours produits × coût interne).
 *
 * Pure helpers, shared by the account menu (AppShell) and the Finance screen:
 * where the value comes from, how an entry is read, which screens refresh
 * after a save. Doctrine (~/dev/fsCMO/docs/ux-regles-pascal.md): the missing
 * cost is named as such and opens THIS setting (`/finance-ops?reglage=
 * cout-interne`), never a generic page and never a fake zero.
 */
import type { QueryKey } from '@tanstack/react-query';
import type { ProductionSettingsPayload } from '../../lib/api';
import type { InternalDayCostSource } from '../../lib/types';
import { formatCurrency } from '../../lib/format';
import type { NavTarget } from '../dashboard/dashboardModel';

/** URL identity of the setting on the Finance screen. */
export const INTERNAL_DAY_COST_PARAM = 'reglage';
export const INTERNAL_DAY_COST_PARAM_VALUE = 'cout-interne';
export const INTERNAL_DAY_COST_ROUTE = `/finance-ops?${INTERNAL_DAY_COST_PARAM}=${INTERNAL_DAY_COST_PARAM_VALUE}`;

/** Server bounds (POST /api/settings/internal-day-cost): > 0 and <= 5000. */
export const INTERNAL_DAY_COST_MAX = 5000;

export const INTERNAL_DAY_COST_LABEL = 'Coût interne (€ HT / jour)';
export const INTERNAL_DAY_COST_ACTION_LABEL = 'Renseigner le coût interne';

/** Where « Renseigner le coût interne » leads from any other screen. */
export function internalDayCostTarget(): NavTarget {
  return { route: INTERNAL_DAY_COST_ROUTE, view: 'finance_ops', filters: {} };
}

/**
 * Every screen that reads the economic margin or its metric contract: they
 * all refresh once the cost is saved (the server invalidates its own caches).
 * Prefix keys, so every exercise is refreshed.
 */
export const INTERNAL_DAY_COST_CONSUMER_KEYS: QueryKey[] = [
  ['settings', 'production'],
  ['finance-ops'],
  ['dashboard'],
  ['data-quality'],
  ['nav-counts'],
];

const SOURCE_LABELS: Record<InternalDayCostSource, string> = {
  setting: 'Réglage de l’application',
  environment: 'Variable d’environnement',
  none: 'Aucune',
};

export interface InternalDayCostInfo {
  /** EUR HT per day, null when nothing is configured. */
  value: number | null;
  source: InternalDayCostSource;
  /** « 450 € HT / jour » or « Non configuré ». */
  valueLabel: string;
  /** « Réglage de l’application », « Variable d’environnement » or « Aucune ». */
  sourceLabel: string;
}

export function formatDayCost(value: number): string {
  return `${formatCurrency(value)} HT / jour`;
}

function positiveNumber(value: unknown): number | null {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

/**
 * Reads GET /api/settings/production. A value always comes with its source;
 * without a value the source is « none », whatever the payload claims.
 */
export function readInternalDayCost(payload: ProductionSettingsPayload | undefined | null): InternalDayCostInfo {
  const value = positiveNumber(payload?.internal_day_cost);
  const declared = payload?.internal_day_cost_source;
  const source: InternalDayCostSource = value === null
    ? 'none'
    : declared === 'environment' ? 'environment' : 'setting';
  return {
    value,
    source,
    valueLabel: value === null ? 'Non configuré' : formatDayCost(value),
    sourceLabel: SOURCE_LABELS[source],
  };
}

export type ParsedDayCost = { ok: true; value: number } | { ok: false; error: string };

/** Accepts « 450 », « 450,50 », « 1 200 € »; refuses what the server would refuse. */
export function parseInternalDayCost(raw: string): ParsedDayCost {
  const text = String(raw || '').replace(/[\s  €]/g, '').replace(',', '.');
  if (!text) return { ok: false, error: 'Saisissez un montant en euros HT par jour' };
  if (!/^\d+(\.\d+)?$/.test(text)) return { ok: false, error: 'Montant invalide : chiffres seulement, par exemple 450' };
  const value = Number(text);
  if (!(value > 0)) return { ok: false, error: 'Le coût interne doit être supérieur à 0 €' };
  if (value > INTERNAL_DAY_COST_MAX) {
    return { ok: false, error: `Le coût interne ne peut pas dépasser ${formatCurrency(INTERNAL_DAY_COST_MAX)} HT par jour` };
  }
  return { ok: true, value: Math.round(value * 100) / 100 };
}

/** A refused save, in French, whatever the server or the transport said. */
export function saveErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error || '');
  if (/permission|forbidden|403/i.test(message)) return 'Réservé à l’administrateur';
  if (/internal_day_cost/i.test(message)) {
    return `Montant refusé par le serveur : plus de 0 € et au plus ${formatCurrency(INTERNAL_DAY_COST_MAX)} HT par jour`;
  }
  if (/fibery/i.test(message)) return 'Fibery indisponible : le coût interne n’a pas été enregistré';
  const status = /failed: (\d{3})/.exec(message);
  if (status) return `Le serveur a refusé l’enregistrement (code ${status[1]})`;
  return message || 'Le serveur a refusé l’enregistrement';
}
